import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { money } from './LaMatchResults';
import { parseUtcDate, timeAgo, formatDateTimeEastern } from '../../utils/dates';
import {
  autoChip, dueText, easternText, summaryText, wouldWriteRows, repricedCount, lastStatusInfo, pauseReasonText,
  errorText, ADMIN_DATA_PATH,
} from '../../utils/lionAutoRunner';
import { joinedInfo, memberIdsOf, pendingInfo } from '../../utils/lionAutoGroups';
import { floorLabel } from '../../utils/lionAutoFloors';
import { holdChipText, holdHintText } from '../../utils/lionAutoHold';
import { idAtRank, rankChoice } from '../../utils/lionAutoLadder';
import {
  ANCHOR_OUTLIER_TITLE, anchorOutlierText, outliersDroppedOf, outliersDroppedText,
} from '../../utils/lionAutoOutliers';
import { anchorWord } from '../../utils/lionAutoListings';
import { noCompChip } from '../../utils/lionAutoNoComp';
import LaCheckerImportModal from './LaCheckerImportModal';
import LaTemplatesModal from './LaTemplatesModal';
import LaRankInsert from './LaRankInsert';
import { MAX_GROUPS_PER_EVENT } from '../../utils/lionAutoChecker';

// The event's saved Lion Auto groups as cards: how many sections, how many of
// our listings sit in them right now, matched market listings and the anchor
// comp's L from the latest match. Click a card to select its sections (the
// table then shows its matches); Edit opens it in the editor; Duplicate opens
// a new group with the same sections and filters (groups may share
// sections, e.g. split by the public-notes filter; never the Auto switch).
// A group on Auto (the server re-ladders it on a schedule and writes its
// prices to Skybox on its own) carries a chip with its last run's status and
// age; one the runner paused (Auto switched off by the runner) a red
// "Paused" chip with the reason in its title, so someone fixes it and turns
// Auto back on. Admins also get "Run now" on a group on Auto (the runner's
// own path, dry run included) with its result on the card. Listings that
// newly joined a group in the last 48 h (a new purchase, or a note / row
// edit that now matches) show as "+N new" beside its count, amber while one
// of them is not ranked yet (Auto and the ladder leave its price alone).
// Those that joined after the group was built and are still unranked also
// wait, with no time limit, under an amber "N need approval" line below the
// count: Approve slots one in at its Smart sort spot (before the first
// ranked listing that sorts after it; the ranks after it move up one), At rank…
// (once something is ranked) at the rank typed, numbered as the table shows
// the ranks (that rank and the ones above it move up one), Leave unranked
// keeps it a member whose price nobody changes. Until then Auto and the
// ladder leave it alone, as any unranked one.
// Each card says the floor the group prices with and where it comes from
// ("Floor 130% · Jonas Brothers", "Floor 110% · own"); nothing without one.
// A group with holding on carries a "Hold · 4 live · $500.00" chip ("Hold ·
// 4 live · 15 seats · $500.00" with a Live qty), and one
// on Auto that the no-comp step-down is lowering (no comp, its clock
// running) a "Stepping down 5%/day" chip.
// "Import from Lion Checker" previews the groups the show's Lion Checker
// filters would create or update (LaCheckerImportModal); a group made that
// way carries a "Checker N" chip (its filter, 1-based as on Lion Checker).
// "Templates" saves this show's groups as a template for the artist's other
// dates, or applies one (LaTemplatesModal); a group linked to a template
// carries a "Template" chip.

export const MAX_GROUPS = MAX_GROUPS_PER_EVENT;

// "Auto · dry run · 12m ago" / red "Paused"; nothing for a group off Auto.
// Kind from autoLastStatus exactly (lionAutoRunner.autoChip).
function AutoChip({ g }) {
  const chip = autoChip(g);
  if (!chip) return null;
  return <span className={`la-auto-chip ${chip.kind}`} title={chip.title}>{chip.text}</span>;
}

// "Hold · 4 live · $500.00": holding is on (lionAutoHold); the title says
// what it does. Nothing with holding off.
function HoldChip({ g }) {
  const text = holdChipText(g.criteria);
  if (!text) return null;
  return <span className="la-hold-chip" title={holdHintText(g.criteria.holdLive, g.criteria.holdSeats)}>{text}</span>;
}

// "Stepping down 5%/day": on Auto with no comp, the no-comp step-down is
// lowering the group's prices (lionAutoNoComp); the title says since when
// and the next step. "No comp · nothing to step": no ranked listing could
// step (each needs a B and a cost). Nothing otherwise.
function NoCompChip({ g }) {
  const chip = noCompChip(g);
  if (!chip) return null;
  return <span className="la-hold-chip" title={chip.title}>{chip.text}</span>;
}

// "+2 new": our listings that newly joined the group (the match's `joined`),
// green when all are ranked, amber when not; the title names each one, when
// it joined and whether it is ranked. Nothing when none joined.
function NewChip({ g, result, listingName }) {
  const info = joinedInfo(g, result, listingName);
  if (!info) return null;
  return <span className={`la-new-chip ${info.kind}`} title={info.title}>+{info.count} new</span>;
}

const PENDING_HINT = 'Approve puts each listing at its Smart sort spot (the ranks after it move up one); '
  + 'At rank… puts one at the rank you type (that rank and the ones above it move up one). Leave unranked keeps its price as is.';
const AT_RANK_TITLE = 'Rank it at the number you type; that rank and the ones above it move up one';

// "2 need approval": the group's listings waiting for a pricer (the match's
// `pending`), folded; open, one line per listing with Approve / At rank… /
// Leave unranked, and Approve all when more than one waits. Nothing when none
// wait. Approve and Approve all send smart (each at its Smart sort spot). At
// rank… shows once the ladder ranks a member (rank 1 alone would be Approve)
// and sends the rank as the listing shown there (beforeId), or nothing for
// the last rank, past every ranked one (ranked last, never the Smart sort
// spot). Approvals carry the member ids that numbered the ranks
// (memberIdsOf), so the toast can say where one listing landed. busy: this
// group's review is in flight
// ("…", buttons off); blocked: why no review can start now (another one is
// in flight), or null; approveBlocked: why Approve can't (the group is open
// in the editor, whose Save would drop the new rank), or null. Clicks stay
// off the card (it selects the group).
function PendingApproval({ g, result, listingName, onReview, busy, blocked, approveBlocked }) {
  const [open, setOpen] = useState(false);
  const info = pendingInfo(result, listingName, g.criteria?.ladderIds);
  if (!info) return null;
  const off = busy || !!blocked || !onReview;
  const memberIds = memberIdsOf(result);
  const { ranks, max } = rankChoice(g.criteria?.ladderIds, memberIds);
  const review = (action, ids) => (e) => {
    e.stopPropagation();
    onReview?.(g, action, ids, action === 'approve' ? { smart: true, memberIds } : {});
  };
  const approveAt = (id) => (n) => onReview?.(g, 'approve', [id], { beforeId: idAtRank(ranks, n), rank: n, memberIds });
  const approveTitle = (n) => approveBlocked || blocked
    || (n === 1
      ? "Rank it at its Smart sort spot in this group's ladder (the ranks after it move up one)"
      : `Rank all ${n} at their Smart sort spots in this group's ladder (the ranks after them move up one)`);
  return (
    <div className="la-pending" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="la-pending-toggle"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        title={open ? 'Fold the list' : `Our listings that joined this group after it was built. ${PENDING_HINT}`}
      >
        <span className="la-advanced-caret">{open ? '▾' : '▸'}</span>
        <span className="la-new-chip warn">{info.count} need{info.count === 1 ? 's' : ''} approval</span>
        {busy && <span className="la-hint">…</span>}
      </button>
      {open && (
        <div className="la-pending-body">
          <div className="la-hint">{PENDING_HINT}</div>
          <ul className="la-pending-list">
            {info.items.map(item => (
              <li key={item.inventoryId} className="la-pending-item">
                <span className="la-pending-label" title={item.label}>{item.label}</span>
                <span className="la-pending-actions">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={off || !!approveBlocked}
                    title={approveTitle(1)}
                    onClick={review('approve', [item.inventoryId])}
                  >
                    {busy ? '…' : 'Approve'}
                  </button>
                  {max > 1 && (
                    <LaRankInsert
                      label="At rank…"
                      max={max}
                      onSubmit={approveAt(item.inventoryId)}
                      disabled={off || !!approveBlocked}
                      title={approveBlocked || blocked || AT_RANK_TITLE}
                      listingName={item.name}
                      buttonClass="btn btn-secondary btn-sm"
                    />
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={off}
                    title={blocked || 'Keep it in the group, unranked: its price stays as it is, and it leaves this list'}
                    onClick={review('dismiss', [item.inventoryId])}
                  >
                    {busy ? '…' : 'Leave unranked'}
                  </button>
                </span>
              </li>
            ))}
          </ul>
          {info.count > 1 && (
            <button
              type="button"
              className="btn btn-secondary btn-sm la-pending-all"
              disabled={off || !!approveBlocked}
              title={approveTitle(info.count)}
              onClick={review('approve', info.ids)}
            >
              {busy ? '…' : `Approve all ${info.count}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// The last "Run now" of a card: its counts (a dry run: "would reprice N",
// with the prices it would write folded away), or why it failed (no Auto
// login: a link to Admin → Data, where a super admin sets it up).
// run: { summary, group, error, needsLogin, at }; listingName(id) names our listing.
function AutoRunResult({ run, listingName, onDismiss }) {
  const { summary, group, error } = run;
  const status = group?.autoLastStatus || null;
  let kind = lastStatusInfo(status).kind;
  if (error) kind = 'bad';
  const rows = summary?.dryRun ? wouldWriteRows(summary) : [];
  const paused = status === 'paused' ? pauseReasonText(group?.autoLastSummary || summary) : null;
  const runError = !error && status === 'error' ? errorText(summary?.error) : '';
  return (
    <div className={`la-auto-result ${kind}`} onClick={(e) => e.stopPropagation()}>
      <div className="la-auto-result-head">
        <strong>{error ? 'Run failed' : summary?.dryRun ? 'Dry run' : 'Ran'}</strong>
        {run.at && <span className="la-hint">{timeAgo(run.at)}</span>}
        <button type="button" className="la-link la-push-right" onClick={onDismiss} title="Hide this result">Dismiss</button>
      </div>
      {error ? <div>{error}</div> : <div>{summaryText(summary, status) || 'No counts came back.'}</div>}
      {error && run.needsLogin && (
        <div><Link to={ADMIN_DATA_PATH} target="_blank" rel="noopener noreferrer">Open Admin → Data</Link></div>
      )}
      {runError && <div>Error: {runError}</div>}
      {status === 'paused' && <div>The runner paused this group{paused ? `: ${paused}` : ''}. Fix it, then turn Auto back on.</div>}
      {rows.length > 0 && (
        <details>
          <summary>The {rows.length} price{rows.length === 1 ? '' : 's'} it would write</summary>
          <ul>
            {rows.map((r, i) => (
              <li key={`${r.inventoryId}-${i}`}>
                {listingName(r.inventoryId) || `#${r.inventoryId}`}: {money(r.from)} → <strong>{money(r.to)}</strong> B
              </li>
            ))}
          </ul>
        </details>
      )}
      {!error && summary?.dryRun && !rows.length && repricedCount(summary) > 0 && (
        <div className="la-hint">The server sent no list of the prices.</div>
      )}
    </div>
  );
}

// "by Albert · updated 5m ago". Stamps are SQLite UTC strings, which
// new Date() would read as browser-local, so they go through parseUtcDate.
function authorLine(g) {
  const updated = parseUtcDate(g.updatedAt);
  return [g.createdByName ? `by ${g.createdByName}` : null, updated ? `updated ${timeAgo(updated)}` : null]
    .filter(Boolean).join(' · ');
}

// "12 sections · your listings 4". Members come from the match (our listings
// in the sections as of the latest pricing read), not from the saved group.
function countsLine(g, result, matchesPending) {
  const n = g.sectionCount ?? (g.criteria?.sections || []).length;
  const sections = `${n} section${n === 1 ? '' : 's'}`;
  if (!result) return `${sections} · your listings ${matchesPending ? '…' : '?'}`;
  const own = result.totals?.members ?? (result.members || []).length;
  return `${sections} · your listings ${own}`;
}

// "3 market matches · anchor comp $120.00 L · 2nd cheapest · 1 low outlier
// dropped" ("zone anchor" when the zone fallback priced the group off its
// sections' zone); with no anchor comp, how many low outliers the rule
// dropped (if any)
function matchLine(result, matchesPending) {
  if (!result) return matchesPending ? 'Matching…' : 'No match result yet';
  const matched = result.totals?.matched ?? (result.comps || []).length;
  const comps = `${matched} market match${matched === 1 ? '' : 'es'}`;
  const dropped = outliersDroppedOf(result);
  if (!result.anchorComp) {
    const droppedText = outliersDroppedText(dropped);
    return <>{comps} · no anchor comp{droppedText && ` · ${droppedText}`}</>;
  }
  const anchorNote = anchorOutlierText(dropped, result.anchorComp.scope);
  return (
    <>
      {comps} · {anchorWord(result.anchorComp)} <strong className="la-gold">{money(result.anchorComp.unitPrice)} L</strong>
      {anchorNote && <span title={ANCHOR_OUTLIER_TITLE}> · {anchorNote}</span>}
    </>
  );
}

const NO_RUNS = {};
const noName = () => null;

/**
 * @param {function} [props.onHoverGroup]    (groupId | null) => void: the mouse entered a
 *   group's card (its id) or left it (null), so the page lights its sections on the map
 * Auto props (admins; canRunAuto false hides them all):
 * @param {boolean} [props.canRunAuto]       show "Run now" on groups on Auto
 * @param {function} [props.onRunAuto]       (group) => void
 * @param {number|null} [props.runningAutoId] the group whose run is in flight
 * @param {string|null} [props.autoRunBlocked] why no run can start now (the runner is off), or null
 * @param {object} [props.autoRuns]          group id → its last Run now { summary, group, error, at }
 * @param {function} [props.onDismissAutoRun] (groupId) => void
 * @param {object} [props.autoDue]           group id → nextDueAt (the runner's status)
 * @param {function} [props.listingName]     inventory id → "Sec 112 Row A", or null
 * Needs approval (anyone who can edit the group; no handler = the list shows
 * with its buttons off):
 * @param {function} [props.onReviewPending] (group, 'approve' | 'dismiss', inventoryIds, at?) => void;
 *   at = { smart: true, memberIds } for Approve / Approve all (each at its Smart sort spot), or
 *   { beforeId, rank, memberIds } for At rank… (beforeId: the listing shown at that rank, null =
 *   the end); memberIds: the Set of member id strings that numbered the ranks, memberIdsOf, so the
 *   answer's ladder gives the rank one listing landed at (rankOf)
 * @param {number|null} [props.reviewingId]  the group whose review is in flight
 * @param {object|null} [props.inheritedFloor] the floor a group with none of its
 *   own uses ({ pct, source, performerName }, inheritedFloorOf), null while unknown
 * Import from Lion Checker (LaCheckerImportModal) and Templates (LaTemplatesModal):
 * @param {string} [props.eventId]          the event (Skybox id) the groups belong to
 * @param {string|null} [props.vividUrl]    the page's VividSeats link (safeUrl-checked), a
 *   fallback when Skybox has none, or null
 */
function LaGroupList({
  groups, results = {}, matchesPending, selectedId, editing, editingId,
  onSelect, onHoverGroup, onEdit, onDuplicate, onDelete, onNew, selectionCount = 0, deletingId, canCreate, createHint, isLoading,
  canRunAuto = false, onRunAuto, runningAutoId = null, autoRunBlocked = null, autoRuns = NO_RUNS,
  onDismissAutoRun, autoDue = null, listingName = noName,
  onReviewPending = null, reviewingId = null, inheritedFloor = null,
  eventId, vividUrl = null,
}) {
  const [importOpen, setImportOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const atLimit = groups.length >= MAX_GROUPS;
  // One new group at a time: a duplicate is a new group too
  const newDraftOpen = editing && editingId == null;
  let duplicateTitle = 'Start a new group with the same sections and filters (Auto off)';
  if (atLimit) duplicateTitle = `An event holds at most ${MAX_GROUPS} groups`;
  else if (newDraftOpen) duplicateTitle = 'Save or cancel the new group first';
  const anyAuto = groups.some(g => g.autoEnabled);
  let importTitle = "Preview the groups this show's Lion Checker filters would create or update";
  if (atLimit) importTitle = `An event holds at most ${MAX_GROUPS} groups`;
  else if (editing) importTitle = 'Save or cancel the open group first';
  const templatesTitle = editing
    ? 'Save or cancel the open group first'
    : "Save this show's groups as a template for the artist's other dates, or apply one";
  return (
    <div className="la-panel">
      <div className="la-panel-head">
        <h3>Pricing groups</h3>
        <span className="la-muted">
          {anyAuto
            ? 'Shared with all staff · prices change when you apply a ladder, and on their own for groups on Auto'
            : 'Shared with all staff · prices change only when you apply a ladder'}
        </span>
        <button
          type="button"
          className="btn btn-primary btn-sm la-push-right"
          onClick={onNew}
          disabled={!canCreate || atLimit || newDraftOpen}
          title={atLimit ? `An event holds at most ${MAX_GROUPS} groups` : (createHint || 'Build a group from sections on the map')}
        >
          {selectionCount > 0 ? `+ New group from selection (${selectionCount})` : '+ New group'}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setImportOpen(true)}
          disabled={isLoading || atLimit || editing}
          title={importTitle}
        >
          Import from Lion Checker
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setTemplatesOpen(true)}
          disabled={isLoading || editing}
          title={templatesTitle}
        >
          Templates
        </button>
      </div>
      {isLoading ? (
        <div className="la-empty">Loading groups…</div>
      ) : groups.length === 0 && !editing ? (
        <div className="la-empty">No groups for this event yet. {canCreate ? 'Pick sections on the map or a zone, then “Save as group”.' : ''}</div>
      ) : (
        <div className="la-groups">
          {newDraftOpen && (
            <div className="la-group-card selected editing">
              <div className="la-group-name">New group</div>
              <div className="la-group-meta la-muted">Not saved yet</div>
            </div>
          )}
          {groups.map(g => {
            const result = results[String(g.id)];
            const warn = (result?.status || []).length > 0;
            const isEditing = editing && editingId === g.id;
            const isSelected = editing ? isEditing : selectedId === g.id;
            const floor = floorLabel(g.criteria?.ladderFloorPct, inheritedFloor);
            return (
              <div
                key={g.id}
                className={`la-group-card${isSelected ? ' selected' : ''}${isEditing ? ' editing' : ''}`}
                onClick={() => onSelect(g.id)}
                onMouseEnter={() => onHoverGroup?.(g.id)}
                onMouseLeave={() => onHoverGroup?.(null)}
              >
                <div className="la-group-name">
                  {g.name}
                  <AutoChip g={g} />
                  <HoldChip g={g} />
                  <NoCompChip g={g} />
                  {g.checkerFilterIndex != null && (
                    <span className="la-reason-chip" title={`Imported from Lion Checker filter ${g.checkerFilterIndex + 1}`}>
                      Checker {g.checkerFilterIndex + 1}
                    </span>
                  )}
                  {g.templateId != null && (
                    <span
                      className="la-reason-chip"
                      title={`Made from a template${g.templateVersion != null ? ` (version ${g.templateVersion})` : ''}: `
                        + "applying it again updates this group's sections, rows, Qty, splits and notes filter"}
                    >
                      Template
                    </span>
                  )}
                  {warn && <span className="la-warn-dot" title={result.status.join(', ')}>!</span>}
                  {isEditing && <span className="la-muted"> · editing</span>}
                </div>
                <div className="la-group-meta">
                  {countsLine(g, result, matchesPending)}
                  <NewChip g={g} result={result} listingName={listingName} />
                </div>
                <PendingApproval
                  g={g}
                  result={result}
                  listingName={listingName}
                  onReview={onReviewPending}
                  busy={reviewingId != null && reviewingId === g.id}
                  blocked={reviewingId != null && reviewingId !== g.id ? 'Another approval is being saved' : null}
                  approveBlocked={isEditing ? 'You are editing this group: rank it in the table, or save / cancel first' : null}
                />
                <div className="la-group-meta">{matchLine(result, matchesPending)}</div>
                {floor && <div className="la-group-meta la-group-floor" title={floor.title}>{floor.text}</div>}
                {canRunAuto && g.autoEnabled && (
                  <div className="la-auto-run" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={runningAutoId != null || !!autoRunBlocked}
                      title={autoRunBlocked || (runningAutoId != null && runningAutoId !== g.id
                        ? 'Another run is in progress'
                        : 'Run this group now, the way the runner does (a dry run while the runner is in dry run)')}
                      onClick={() => onRunAuto?.(g)}
                    >
                      {runningAutoId === g.id ? 'Running…' : 'Run now'}
                    </button>
                    {autoDue && String(g.id) in autoDue && (
                      <span className="la-hint" title={easternText(autoDue[String(g.id)]) || undefined}>
                        next run {dueText(autoDue[String(g.id)])}
                      </span>
                    )}
                  </div>
                )}
                {canRunAuto && autoRuns[String(g.id)] && (
                  <AutoRunResult
                    run={autoRuns[String(g.id)]}
                    listingName={listingName}
                    onDismiss={() => onDismissAutoRun?.(g.id)}
                  />
                )}
                <div className="la-group-foot">
                  <span className="la-muted" title={g.updatedAt ? `Updated ${formatDateTimeEastern(g.updatedAt)} ET` : undefined}>{authorLine(g)}</span>
                  <span className="la-group-actions">
                    <button type="button" className="btn btn-secondary btn-sm" disabled={isEditing} onClick={(e) => { e.stopPropagation(); onEdit(g); }}>Edit</button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={atLimit || newDraftOpen}
                      title={duplicateTitle}
                      onClick={(e) => { e.stopPropagation(); onDuplicate(g); }}
                    >
                      Duplicate
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" disabled={deletingId === g.id} onClick={(e) => { e.stopPropagation(); onDelete(g); }}>
                      {deletingId === g.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {importOpen && (
        <LaCheckerImportModal
          eventId={eventId}
          vividUrl={vividUrl}
          groups={groups}
          onClose={() => setImportOpen(false)}
        />
      )}
      {templatesOpen && (
        <LaTemplatesModal eventId={eventId} groups={groups} onClose={() => setTemplatesOpen(false)} />
      )}
    </div>
  );
}

export default LaGroupList;
